import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useQuery from 'hooks/useQuery';

// styles
import { CollaboratorsContainer } from './styles';

// Redux actions
import { apiGetAllOrgRobotsRequest, apiUpdateOrgRobotRequest, apiGetAllOrgInstanceRequest, apiUpdateOrgInstanceRequest, apiGetAllOrgOnPremServersRequest, apiUpdateOrgOnPremServerRequest, } from 'Redux/actions/gear';
import { resetInvite } from 'Redux/actions/invite';

// Components
import GearsInfoSection from './components/GearsInfoSection';
import OnboardInstance from './components/OnboardInstance';
import EditOrgRobot from '../../Components/EditOrgGears/EditOrgRobot';
import EditOrgInstance from '../../Components/EditOrgGears/EditOrgInstance';
import EditOnPremServer from 'Components/EditOrgGears/EditOrgOnPremServer';
import Loader from 'Components/Loader';
import OnboardRobot from './components/OnboardRobot';
import GearAssetsHeader from './components/GearAssetsHeader';
import OnboardOnPremServer from './components/OnboardOnPremServer';

// Config
import { GearTypes, deviceTypeFilterOptions, gearSubscriptionStatusFilterOptions } from 'Config/gearAssetsConstant';

const GearAssetsPage = ({ organisationId, organisationName }) => {
  const dispatch = useDispatch();
  const params = useQuery();

  // useSelector
  const gearState = useSelector(state => state.gear);
  const auth = useSelector(state => state.auth);
  const invite = useSelector(state => state.invite);

  const [sideNavPage, setSideNavPage] = useState('');
  const [allOrgGears, setAllOrgGears] = useState([]);
  const [postsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectGear, setSelectGear] = useState(null);

  const [nameFilter, setNameFilter] = useState('');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState(deviceTypeFilterOptions[0]);
  const [subStatusFilter, setSubStatusFilter] = useState(gearSubscriptionStatusFilterOptions[0]);

  const handleNameFilterChange = value => {
    setCurrentPage(1);
    setNameFilter(value);
  };

  const handleDeviceTypeFilterChange = option => {
    setCurrentPage(1);
    setDeviceTypeFilter(option);
  };

  const handleSubStatusFilterChange = option => {
    setCurrentPage(1);
    setSubStatusFilter(option);
  };

  const onChangeRightNav = e => {
    setSideNavPage(e);
  };

  const closeSideBar = () => {
    setSideNavPage('');
  };

  const editGear = (data, id) => {
    setSelectGear(data);
    if (data?.device_details?.device_type === GearTypes.INSTANCE.deviceType) {
      onChangeRightNav('EditOrgInstance');
    } else if (data?.device_details?.device_type === GearTypes.ON_PREM_SERVER.deviceType) {
      onChangeRightNav('EditOrgOnPremServer');
    } else {
      onChangeRightNav('EditOrgRobot');
    }
  };

  const onGearEditApi = (updateData, isDelete = false) => {
    if (updateData?.device_details?.device_type === GearTypes.INSTANCE.deviceType) {
      dispatch(apiUpdateOrgInstanceRequest(auth.token, updateData))
    } else if (updateData?.device_details?.device_type === GearTypes.ON_PREM_SERVER.deviceType) {
      dispatch(apiUpdateOrgOnPremServerRequest(auth.token, updateData))
    } else {
      dispatch(apiUpdateOrgRobotRequest(auth.token, updateData))
    }
  };

  // useEffect(() => {
  //   if (gearState.redirect_url) {
  //     window.location.replace(gearState.redirect_url);
  //   }
  // }, [gearState.redirect_url]);

  useEffect(() => {
    let tempGears = [
      ...(gearState?.orgOnPremServers?.map(server => ({ ...server }))|| []),
      ...(gearState?.orgInstances?.map(instance => ({ ...instance })) || []),
      ...(gearState?.orgRobots?.map(robot => ({ ...robot })) || []),
    ];

    setAllOrgGears(tempGears);
  }, [gearState?.orgInstances, gearState?.orgRobots, gearState?.orgOnPremServers]);

  useEffect(() => {
    if (
      params.get('payment_status') &&
      params.get('payment_status') === 'success'
    ) {
      toast.success('Payment was made successfully.', { toastId: 'success' });
    }

    if (
      params.get('payment_status') &&
      params.get('payment_status') === 'cancel'
    ) {
      toast.error('The payment was canceled.', { toastId: 'cancel' });
    }

    window.history.pushState({}, document.title, window.location.pathname);

    dispatch(apiGetAllOrgRobotsRequest(auth.token, organisationId));
    dispatch(apiGetAllOrgInstanceRequest(auth.token, organisationId));
    dispatch(apiGetAllOrgOnPremServersRequest(auth.token, organisationId));
  }, [organisationId]);

  useEffect(() => {
    if (invite.success) {
      dispatch(resetInvite());
    }
  }, [invite]);

  const rightSideNavPageObj = {
    'OnboardInstance': (
      <OnboardInstance
        onChangeRightNav={onChangeRightNav}
        organisationId={organisationId}
        organisationName={organisationName}
      />
    ),
    'EditOrgInstance': (
      <EditOrgInstance
        editInstance={selectGear}
        closeSideBar={closeSideBar}
        onGearEditApi={onGearEditApi}
      />
    ),
    'OnboardOnPremServer': (
      <OnboardOnPremServer
        onChangeRightNav={onChangeRightNav}
        organisationId={organisationId}
        organisationName={organisationName}
      />
    ),
    'EditOrgOnPremServer': (
      <EditOnPremServer
        editOnPremServer={selectGear}
        closeSideBar={closeSideBar}
        onGearEditApi={onGearEditApi}
      />
    ),
    'OnboardRobot': (
      <OnboardRobot
        onChangeRightNav={onChangeRightNav}
        organisationId={organisationId}
        organisationName={organisationName}
      />
    ),

    'EditOrgRobot': (
      <EditOrgRobot
        selectedRobot={selectGear}
        onGearEditApi={onGearEditApi}
        closeSideBar={closeSideBar}
      />
    ),
  };

  return gearState?.isLoading ? (
    <Loader />
  ) : (
    <CollaboratorsContainer>
      <GearAssetsHeader
        onChangeRightNav={onChangeRightNav}
        gears={gearState}
      />

      <GearsInfoSection
        allOrgGears={allOrgGears}
        postsPerPage={postsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        editGear={editGear}
        handleNameFilterChange={handleNameFilterChange}
        handleDeviceTypeFilterChange={handleDeviceTypeFilterChange}
        handleSubStatusFilterChange={handleSubStatusFilterChange}
        nameFilter={nameFilter}
        deviceTypeFilter={deviceTypeFilter}
        subStatusFilter={subStatusFilter}
      />

      {sideNavPage in rightSideNavPageObj
        ? rightSideNavPageObj[sideNavPage]
        : null
      }
    </CollaboratorsContainer>
  );
};

export default GearAssetsPage;
